import { Box, Grid, Stack, Typography } from '@mui/material';
import NextImage from 'next/image';
import NextLink from 'next/link';
import PropTypes from 'prop-types';

import InstantQuoteChip from '@/components/InstantQuoteChip/InstantQuoteChip';
import RichText from '@/components/RichText/RichText';
import SaleChip from '@/components/SaleChip/SaleChip';
import StrapiImage from '@/components/StrapiImage/StrapiImage';
import Button from '@/components/ui/Button/Button';

import {
  Card as CardBase,
  CardActionArea,
  CardContent,
  CardMedia,
  DescriptionText,
} from './Card.style';

const Card = ({ card }) => {
  const {
    headline,
    subtitle,
    description,
    showSale,
    image,
    sendAutomatedOffer,
    mainActionOnClick,
    mainActionLink,
    mainActionButtonLabel,
    secondActionOnClick,
    secondActionButtonLabel,
    'data-test-subject': dataTestSubject,
  } = card;

  const getColumnConfig = () => {
    return {
      lg: 4,
      sm: 6,
      xs: 12,
    };
  };

  return (
    <Grid
      item
      {...getColumnConfig()}
      display="flex"
      data-test-subject={dataTestSubject || 'card-grid'}>
      <CardBase
        variant="outlined"
        sx={{
          position: 'relative',
        }}>
        {(sendAutomatedOffer || showSale) && (
          <Stack
            gap={1}
            sx={{
              position: 'absolute',
              left: 16,
              top: 16,
              '& > *': {
                zIndex: 1,
              },
            }}
            alignItems="flex-start">
            {sendAutomatedOffer && <InstantQuoteChip />}
            {showSale && <SaleChip />}
          </Stack>
        )}
        <CardActionArea
          onClick={mainActionOnClick}
          {...(mainActionLink
            ? {
                component: NextLink,
                href: mainActionLink,
              }
            : {})}>
          <CardMedia centerImage>
            {typeof image === 'object' ? (
              <StrapiImage
                data={image?.data}
                sizes="(max-width: 548px) 100vw, 360px"
                fill
                style={{ objectFit: 'cover' }}
              />
            ) : (
              <NextImage
                src={image}
                alt=""
                fill
                sizes="(max-width: 548px) 100vw, 360px"
                style={{ objectFit: 'cover' }}
              />
            )}
          </CardMedia>
          <CardContent>
            <Typography
              variant="h5Redesign"
              component="div"
              color="primary.dark"
              dangerouslySetInnerHTML={{ __html: headline }}
              data-test-subject="headline"
            />
            {subtitle &&
              (typeof subtitle === 'string' ? (
                <Typography
                  variant="h6Redesign"
                  component="div"
                  color="primary.main"
                  dangerouslySetInnerHTML={{ __html: subtitle }}
                  data-test-subject="subtitle"
                />
              ) : (
                subtitle
              ))}
            {description && (
              <DescriptionText data-test-subject="description">
                <RichText data={description} />
              </DescriptionText>
            )}
          </CardContent>
          {mainActionButtonLabel && (
            <Box sx={{ p: 2, pt: 0, width: '100%' }}>
              <Button
                data-test-subject="primary-action-button"
                component="div"
                size="large"
                color="primary"
                fullWidth
                disableElevation>
                {mainActionButtonLabel}
              </Button>
            </Box>
          )}
        </CardActionArea>
        {secondActionOnClick && (
          <Box sx={{ p: 2, pt: 0 }}>
            <Button
              data-test-subject="secondary-action-button"
              size="large"
              color={mainActionButtonLabel ? 'primaryLight' : 'primary'}
              onClick={secondActionOnClick}
              fullWidth
              disableElevation>
              {secondActionButtonLabel}
            </Button>
          </Box>
        )}
      </CardBase>
    </Grid>
  );
};

Card.propTypes = {
  card: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    description: PropTypes.string,
    showSale: PropTypes.bool,
    image: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        data: PropTypes.shape({
          attributes: PropTypes.shape({
            url: PropTypes.string,
          }),
        }),
      }),
    ]),
    mainActionLink: PropTypes.string,
    mainActionOnClick: PropTypes.func,
    mainActionButtonLabel: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.element,
    ]),
    secondActionOnClick: PropTypes.func,
    secondActionButtonLabel: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.element,
    ]),
    'data-test-subject': PropTypes.string,
  }),
};

export default Card;
